import { CommonModule } from "@angular/common";
import { Component, inject } from "@angular/core";
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { RouterLink } from "@angular/router";

type Status = "idle" | "sending" | "sent";

@Component({
  selector: "app-contact",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  templateUrl: "./contact.component.html",
  styleUrl: "./contact.component.scss",
})
export class ContactComponent {
  private readonly fb = inject(NonNullableFormBuilder);

  readonly supportEmail = "info@gloget.com";
  readonly supportPhoneDisplay = "+1 (415) 555-0142";
  readonly supportPhoneHref = "tel:+14155550142";

  readonly audiences = [
    { key: "customer", label: "A customer" },
    { key: "salon", label: "A salon owner" },
    { key: "partner", label: "A partner or press" },
    { key: "other", label: "Something else" },
  ];

  readonly preferredMethods = [
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
  ];

  status: Status = "idle";
  submitted = false;

  contactForm = this.fb.group({
    name: ["", [Validators.required, Validators.minLength(2)]],
    email: ["", [Validators.required, Validators.email]],
    phone: ["", []],
    audience: ["customer", [Validators.required]],
    subject: ["", [Validators.required, Validators.minLength(3)]],
    message: ["", [Validators.required, Validators.minLength(10)]],
    preferred: ["email", [Validators.required]],
  });

  get isSending() {
    return this.status === "sending";
  }

  get isSent() {
    return this.status === "sent";
  }

  isInvalid(field: "name" | "email" | "subject" | "message"): boolean {
    const control = this.contactForm.get(field);
    if (!control) {
      return false;
    }
    return control.invalid && (this.submitted || control.touched);
  }

  submit(): void {
    this.submitted = true;

    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    this.status = "sending";

    // This landing-page form has no dedicated backend endpoint wired up yet.
    // Swap this simulated delay for a real request when one becomes available,
    // e.g. this.http.post(`${apiUrl}/api/contact`, this.contactForm.getRawValue())
    setTimeout(() => {
      this.status = "sent";
    }, 900);
  }

  resetForm(): void {
    this.contactForm.reset();
    this.submitted = false;
    this.status = "idle";
  }
}
